import { Injectable } from '@angular/core';
import { ScriptService } from './scripts.service';
import { Subject } from 'rxjs';
import { retry } from '@care-portals/shared';

declare var CollectJS: any;

export interface CollectJsTokenizeResponse {
  tokenType: string;
  token: string;
  card: {
    number: string;
    bin: string;
    exp: string;
    type: string;
    hash: string;
  };
}
/*
  This service is responsible for initializing the CollectJS library and handling the tokenization process.
  It provides methods to initialize the library, handle tokenization, and manage the tokenization promise.
  NOTE: this service is a singleton and should only be used in one place
  */
@Injectable({
  providedIn: 'root'
})
export class CollectJsService {
  isLoading: Subject<boolean>;
  fieldsValidation: Subject<{
    field: string;
    status: boolean;
    message: string;
  }>;
  checkoutToken: string;
  saveCardToken: string;
  tokenizationPromise: Promise<{ token: string; [key: string]: string }>;
  private tokenizationPromiseResolve: (value: any) => void;
  private tokenizationPromiseReject: (reason?: any) => void;
  private additionalFieldsLinker: () => { [key: string]: string };
  constructor(private readonly scriptService: ScriptService) {
    this.isLoading = new Subject<boolean>();
    this.fieldsValidation = new Subject<{
      field: string;
      status: boolean;
      message: string;
    }>();
  }
  requiredAdditionalFields = ['zip'];

  initializeCollectJs(
    nmiPublicKey: string,
    additionalFieldsLinker: () => { [key: string]: string }
  ) {
    this.isLoading.next(true);
    this.additionalFieldsLinker = additionalFieldsLinker;
    this.scriptService
      .loadScript({
        name: 'collectjs',
        src: 'https://secure.nmi.com/token/Collect.js',
        dataset: {
          tokenizationKey: nmiPublicKey,
          variant: 'inline',
          styleSniffer: 'false'
        }
      })
      .then(() =>
        retry(() => {
          const requiredFields = ['ccnumber', 'ccexp', 'cvv', 'zip'];
          const missingFields = requiredFields.filter(
            field => !document.querySelector(`#${field}`)
          );
          if (missingFields.length > 0) {
            console.error(
              `Missing required fields in DOM: ${missingFields.join(', ')}`
            );
            this.isLoading.next(false);
            throw new Error(
              `Missing required fields in DOM: ${missingFields.join(', ')}`
            );
          }
        })
      )
      .then(() => {
        CollectJS.configure({
          styleSniffer: 'false',
          variant: 'inline',
          googleFont: 'Montserrat:400',
          customCss: {
            padding: '8px 11px',
            'font-size': '16px',
            border: '1px solid #0b3bd9',
            'border-radius': '4px'
          },
          invalidCss: {
            'border-color': 'red'
          },
          validCss: {},
          placeholderCss: {
            color: '#bfbfbf'
          },
          focusCss: {
            'border-color': '#ffac9c'
          },
          fields: {
            ccnumber: {
              title: 'Card Number',
              placeholder: '0000 0000 0000 0000',
              enableCardBrandPreviews: true
            },
            ccexp: {
              title: 'Expiration',
              placeholder: 'MM/YYYY'
            },
            cvv: {
              display: 'required',
              title: 'CVV Code',
              placeholder: '***'
            }
          },
          fieldsAvailableCallback: () => {
            this.isLoading.next(false);
          },
          validationCallback: (field, status, message) => {
            this.fieldsValidation.next({ field, status, message });
            if (status === false && this.tokenizationPromiseReject) {
              this.tokenizationPromiseReject(
                new Error(`Billing info is missing: ${field} is required`)
              );
            }
          },
          timeoutDuration: 10000,
          timeoutCallback: () => {
            this.tokenizationPromiseReject(
              "The tokenization didn't respond in the expected timeframe.  This could be due to an invalid or incomplete field or poor connectivity"
            );
          },
          callback: ({ token, card, tokenType }: CollectJsTokenizeResponse) => {
            this.tokenizationPromiseResolve({
              token,
              card,
              tokenType,
              ...(this.additionalFieldsLinker() || {})
            });
          }
        });
      })
      .catch(err => {
        console.error('Error initializing CollectJS', err);
        this.isLoading.next(false);
        throw err;
      });
  }
  validateAdditionalFields(additionalFields: { [key: string]: string }) {
    for (const field of this.requiredAdditionalFields) {
      if (!additionalFields[field]) {
        return false;
      }
    }
    return true;
  }

  tokenize(
    type: 'place order' | 'save payment method' = 'place order'
  ): Promise<CollectJsTokenizeResponse & { zip: string }> {
    return new Promise((resolve, reject) => {
      const additionalFields = this.additionalFieldsLinker();
      for (const field of this.requiredAdditionalFields) {
        if (!additionalFields[field]) {
          console.log('stopping tokenization');
          return reject(
            new Error(`Missing Billing Info: ${field} is required`)
          );
        }
      }
      this.tokenizationPromiseResolve = resolve;
      this.tokenizationPromiseReject = reject;

      CollectJS.startPaymentRequest(new Event(type));
    });
  }
}
